/**
 * FlowOverlaySortContext — sort mode for optimizer overlay rows on flow detail.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import type { FlowLayer } from '@trafficpulse/shared';

import { usePreviewRankLookups } from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import type { PreviewRankFilter } from '@/features/flow-optimizer/components/preview/preview-sort';

import {
  sortFlowOverlayItems,
  type FlowOverlayListSortMode,
} from './flow-overlay-sort.util';

interface FlowOverlaySortContextValue {
  sortMode: FlowOverlayListSortMode;
  rankFilter: PreviewRankFilter;
  setSortMode: (mode: FlowOverlayListSortMode) => void;
  setRankFilter: (filter: PreviewRankFilter) => void;
  sortItems: <T extends { id: string }>(
    items: readonly T[],
    layer: FlowLayer,
    pathId?: string,
  ) => T[];
}

const FlowOverlaySortContext = createContext<FlowOverlaySortContextValue | null>(null);

export const FlowOverlaySortProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [sortMode, setSortMode] = useState<FlowOverlayListSortMode>('flow');
  const [rankFilter, setRankFilter] = useState<PreviewRankFilter>('all');
  const lookups = usePreviewRankLookups();

  const sortItems = useCallback(
    <T extends { id: string }>(items: readonly T[], layer: FlowLayer, pathId?: string) => {
      if (!lookups) return [...items];
      return sortFlowOverlayItems(items, {
        mode: sortMode,
        rankFilter,
        lookups,
        layer,
        pathId,
      });
    },
    [lookups, rankFilter, sortMode],
  );

  const value = useMemo(
    () => ({ sortMode, rankFilter, setSortMode, setRankFilter, sortItems }),
    [rankFilter, sortItems, sortMode],
  );

  return (
    <FlowOverlaySortContext.Provider value={value}>{children}</FlowOverlaySortContext.Provider>
  );
};

export function useFlowOverlaySort(): FlowOverlaySortContextValue | null {
  return useContext(FlowOverlaySortContext);
}
